import { useTranslation } from 'react-i18next';

import type { ClientCertificateListItem } from '@/types/api/queries';

import { Skeleton } from '@/components/ui/skeleton';

import { useClientCertificateByCodeQuery } from '@/queries';

import { centsToMoneyFormat } from '@/helpers';

import { CERTIFICATE_KIND_ICONS } from '@/constants';

import { certificateItemCounters } from './utils';

type CertificateCompositionProps = {
  certificate: ClientCertificateListItem;
  byCodeUrl: string;
};

export const CertificateComposition = (props: CertificateCompositionProps) => {
  const { certificate, byCodeUrl } = props;
  const { t } = useTranslation();

  const { data, isLoading } = useClientCertificateByCodeQuery(
    byCodeUrl,
    certificate.code,
    true,
  );

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const detail = data?.certificate;

  if (!detail) {
    return (
      <p className="text-muted-foreground p-2 text-sm">
        {t('No certificate with this code')}
      </p>
    );
  }

  const redeemedBy = detail.redeemed_by.map((client) => client.name).join(', ');

  return (
    <div className="space-y-2 p-2">
      <p className="text-muted-foreground text-sm">
        {`${t('By whom used')}: ${redeemedBy || t('Not used')}`}
      </p>

      {!!detail.note && (
        <p className="text-muted-foreground text-sm">
          {`${t('Note')}: ${detail.note}`}
        </p>
      )}

      <div className="bg-secondary/10 space-y-1 rounded-lg p-2">
        {detail.certificate_items.map((item) => {
          const Icon = CERTIFICATE_KIND_ICONS[item.kind];
          const counters = certificateItemCounters(item);

          return (
            <div
              key={item.id}
              className="bg-background grid min-h-10 grid-cols-[auto_1fr_auto_auto] items-center gap-x-4 rounded-lg border px-3 py-2 shadow-sm"
            >
              <span className="flex size-6 items-center justify-center">
                <Icon className="size-4 text-blue-500 dark:text-blue-400" />
              </span>

              <div className="min-w-0 truncate text-sm font-medium">
                {item.kind === 'deposit'
                  ? t('Deposit')
                  : (item.sellable?.name ?? '—')}
              </div>

              <div className="text-muted-foreground text-right text-[0.7rem]">
                <div>{`${t('Issued')}: ${counters.initial}`}</div>
                <div>{`${t('Remaining')}: ${counters.remaining}`}</div>
              </div>

              <span className="text-sm font-semibold">
                {centsToMoneyFormat(item.remaining_liability_cents)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
